import { useMutation } from '@apollo/client';
import SpinnerButton from '@do/walrus/SpinnerButton';
import TextInput from '@do/walrus/TextInput';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React, { ReactElement, useContext } from 'react';
import * as Yup from 'yup';
import { createClient } from '../client';
import {
  EMAIL_MAX_LEN,
  LOGIN_REDIRECT_URL,
  REGISTER_ENDPOINT
} from '../constants';
import copy from '../copy';
import {
  createAccount as CreateAccountRequest,
  createAccountVariables
} from '../generated-types/createAccount';
import ArkoseContext, { ArkoseState } from '../providers/ArkoseContext';
import { CREATE_ACCOUNT } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { yupPasswordValidation } from '../utils/validation-utils';
import { ErrorBanner } from './Common';

const formCopy = copy.form;

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(formCopy.validation.email_valid)
    .required(formCopy.validation.email_required)
    .max(EMAIL_MAX_LEN, copy.errors.too_long.details),
  password: yupPasswordValidation
});

interface Props {
  email?: string;
  onboardingOrigin?: string;
  sessionId?: string;
  isLoadingOauthPath: boolean;
}
const client = createClient(REGISTER_ENDPOINT);

const RegistrationForm: React.FC<Props> = ({
  email,
  onboardingOrigin,
  sessionId,
  isLoadingOauthPath
}): ReactElement => {
  const [createAccount, { loading: isCreatingAccount }] = useMutation<
    CreateAccountRequest,
    createAccountVariables
  >(CREATE_ACCOUNT, { client });

  const [genericServerError, setGenericServerError] = React.useState(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const { run, state: arkoseState } = useContext(ArkoseContext);

  const isRunningArkose = arkoseState === ArkoseState.PROCESSING;

  return (
    <Formik
      initialValues={{
        name: '',
        password: '',
        email: email || '',
        onboarding_origin: onboardingOrigin || ''
      }}
      validationSchema={FormValidation}
      onSubmit={async (values, { setErrors }) => {
        setGenericServerError(null); /* Reset displayed error */
        try {
          await run(sessionId || '');
          const { data } = await createAccount({
            variables: {
              createReq: values
            }
          });
          const redirect =
            _get(data, 'createAccount.redirect_url') || LOGIN_REDIRECT_URL;

          window.location.assign(redirect);
          setIsRedirecting(true);
        } catch (e) {
          if (e !== undefined) {
            handleFormError(setErrors, setGenericServerError)(e);
          }
        }
      }}
    >
      {({ errors, values, touched }) => (
        <Form autoComplete="none">
          {genericServerError && <ErrorBanner error={genericServerError} />}

          <TextInput
            placeholder={formCopy.name_placeholder}
            name="name"
            label={formCopy.name_label}
            className="do-u-mb--small"
            touched={touched.name}
            error={errors.name}
            value={values.name}
            autoComplete="none"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />

          <TextInput
            placeholder={formCopy.email_placeholder}
            required
            name="email"
            label={formCopy.email_label}
            className="do-u-mb--small"
            touched={touched.email}
            error={errors.email}
            value={values.email}
            autoComplete="none"
          />

          <TextInput
            placeholder={formCopy.register.password_placeholder}
            required
            name="password"
            label={formCopy.password_label}
            type="password"
            className="do-u-mb--medium"
            touched={touched.password}
            error={errors.password}
            value={values.password}
            autoComplete="none"
          />

          <SpinnerButton
            type="submit"
            variation="success"
            fullWidth={true}
            disabled={
              isCreatingAccount ||
              isRedirecting ||
              isRunningArkose ||
              isLoadingOauthPath
            }
            spinning={
              isCreatingAccount ||
              isRedirecting ||
              (isRunningArkose && !isLoadingOauthPath)
            }
            loadingMessage={copy.loading.registration.belowButton}
          >
            {formCopy.register.button_text}
          </SpinnerButton>
        </Form>
      )}
    </Formik>
  );
};

export default RegistrationForm;
